import { ARTIFICER, FORM_BIT, FORKS, HEROES, SKILL_CASTS, SKILL_FIELDS, SKILL_FX, SKILLS, type DamageType, type ForkId, type Lay, type Num, type Place, type SkillDef, type SkillField, type SkillId, type Step, type When, GLYPH_FX, glyphFits, glyphTurns, potency } from '@somnhold/content';
import { briarHedge, briarThorn, contagionOn, cracksOn, driftOn, hiveOn, rootBy, shadeWolves, treeHoldOn, wallHoldOn } from './panoply-druid.ts';
import { axeJump, carterHeal, chargeBack, duelStart, fearMark, furyStart, kickMark, roarEcho } from './panoply-barbarian.ts';
import { atoneChainOn, avatarStart, backFlash, blessShare, hammerLoose, keeperSeal, sacrificeWatch, stairStun, stoneSelf } from './panoply-paladin.ts';
import { bellows, secondDrone, squareShield, squareTuned } from './panoply-artificer.ts';
import { arrivalFlare, boltBack, dripOn, iceTrail, jailLinks, leftImage } from './panoply-mage.ts';
import { bannerLands, bannerSpot, courageOn, decoySwap, echoAgainLater, hookLeap, lowBlowMarked, slamAgainLater, slamClash, standGather, wallPushOn } from './panoply.ts';
import { choirBy, columnWalks, lightFollows, nurseMend, prayerLights, rayStays, renewedBy, returnStates, vigilMark } from './panoply-cleric.ts';
import { deflect, eyeOpens, hiddenGrows, hunterVolley, leapArrows, splinters, timelyLeap, trackerArrows } from './panoply-ranger.ts';
import { cellAtWorld, enemySlideClear, heroWalkable } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { cosA, dist2, iatan2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Command, Enemy, Hero, Structure, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, findEnemy, findHero, healHero, isFlying, pstats, structurePos, targetable, taunt, wardHero } from './combat.ts';
import { corrupt, lifesteal, raiseShield, shiftBear } from './kits.ts';
import { getUp } from './dungeon.ts';
import { isBoss, launch, stunFor } from './towers.ts';
import { fx, hasUq, heavyStun, leapBack, rootSpread, uqBp, whirlPull } from './uniques.ts';
import { featCast, featPull, featStaff } from './feats.ts';
import { ANVIL_FOREVER, aegisCast, axeAfter, bearBegan, cloakLanded, cloakLeft, dawnEverywhere, dawnReach, dawnTouched, fangAgain, fangBlowBp, fangSeen, fangHealBp, formA, formBits, formFinal, gasparGuard, gateRift, gateShut, gateWave, keyExtra, kitSpots, oathHolds, oathReach, parapet, rootsHeld, smiteHeavy, smiteToll, staffLeaps, staffWake } from './forms.ts';
import {
  along,
  clampPoint,
  enemiesInCone,
  enemiesInLine,
  guardTarget,
  healPower,
  heroesInLine,
  hurtBp,
  inspire,
  landing,
  mostHurt,
  nearestEnemy,
  patched,
  poison,
  power,
  presence,
  recordArea,
  shove,
  slowIt,
  smiteOne,
  sting,
  structural,
  wallCells,
  whirlPulse,
} from './skills.ts';

/**
 * Runs a skill's program (docs/PODERES.md §10, stage 2; the programs in `content/skill-casts.ts`, how to read
 * them in `content/skill-cast.ts`). Each program is compiled to functions the first time its skill is cast;
 * what runs every tick (zones, summons, projectiles) is untouched by it.
 */

type SkillCmd = Extract<Command, { t: 'skill' }>;
type Pt = { x: number; y: number };

interface Cast {
  s: BattleState;
  h: Hero;
  sk: SkillDef;
  fork: ForkId | null;
  cmd: SkillCmd;
  px: number;
  py: number;
  p: Pt | null;
  aim: Pt | null;
  e: Enemy | null;
  a: Hero | null;
  land: Pt | null;
  end: Pt | null;
  q: Pt | null;
  o: Enemy | null;
  ho: Hero | null;
  /** Where an aegis went (its event goes there). */
  g: Pt | null;
  foes: Record<string, Enemy[]>;
  friends: Record<string, Hero[]>;
  counted: Record<string, number>;
  vars: Record<string, number>;
  spots: Pt[] | null;
  cells: number[] | null;
  killed: boolean;
  cd: number;
  /** The damage type a conversion glyph on this button turns all the skill deals into (docs/GLIFOS.md §1). */
  conv: DamageType | null;
  /** A pack glyph on this button: one summon more (the first `bot` step lays it). */
  pack: boolean;
  /** An echo glyph's second cast (it counts for nothing a cast counts, the flasks included). */
  again?: boolean;
}

/** How a program step ends: on to the next, the cast comes to nothing, the cast is done early, or on to the next one caught. */
type Flow = 0 | 1 | 2 | 3;
const GO: Flow = 0;
const FAIL: Flow = 1;
const STOP: Flow = 2;
const SKIP: Flow = 3;

const OMIT = Symbol('omit');

/*
 * Each program is compiled once, the first time its skill is cast, into functions that close over its numbers:
 * a cast then runs no lookups on the data, and every object it reads has one shape (RENDIMIENTO.md, La
 * simulación). The compiled functions are pure translations: the same program gives the same cast.
 */
type F<T> = (c: Cast) => T;

const placeOf = (p: Place): F<Pt | null> => {
  switch (p) {
    case 'h':
      return (c) => c.h;
    case 'p':
      return (c) => c.p;
    case 'aim':
      return (c) => c.aim;
    case 'e':
      return (c) => c.e;
    case 'a':
      return (c) => c.a;
    case 'o':
      return (c) => c.o;
    case 'ho':
      return (c) => c.ho;
    case 'land':
      return (c) => c.land;
    case 'end':
      return (c) => c.end;
    case 'q':
      return (c) => c.q;
  }
};
const ptOf = (p: Place): F<Pt> => placeOf(p) as F<Pt>;
const heroOf = (p: Place): F<Hero> => (p === 'h' ? (c) => c.h : p === 'a' ? (c) => c.a as Hero : (c) => c.ho as Hero);

const FIELD: Record<SkillField, F<number>> = {
  damage: (c) => c.sk.damage,
  radius: (c) => c.sk.radius,
  duration: (c) => c.sk.duration,
  stun: (c) => c.sk.stun,
  slowBp: (c) => c.sk.slowBp,
  amount: (c) => c.sk.amount,
  every: (c) => c.sk.every,
  castRange: (c) => c.sk.castRange,
  cooldown: (c) => c.sk.cooldown,
};

/** A number of the program, as a function of the cast. */
const numOf = (n: Num): F<number> => {
  if (typeof n === 'number') return () => n;
  if (typeof n === 'string') return FIELD[n];
  if ('pow' in n) {
    const v = numOf(n.pow);
    return (c) => power(c.h, v(c));
  }
  if ('fork' in n) {
    const by = new Map<ForkId, F<number>>();
    for (const k in n.fork) by.set(k as ForkId, numOf(n.fork[k as ForkId] as Num));
    const other = numOf(n.else);
    return (c) => ((c.fork ? by.get(c.fork) : undefined) ?? other)(c);
  }
  if ('mul' in n) {
    const [a, b] = [numOf(n.mul[0]), numOf(n.mul[1])];
    return (c) => mulBp(a(c), b(c));
  }
  if ('v' in n) {
    const k = n.v;
    return (c) => c.vars[k] ?? 0;
  }
  if ('fx' in n) {
    const { fx: id, key } = n;
    return (c) => fx(id, c.h)[key] as number;
  }
  if ('uq' in n) {
    const [id, t, e] = [n.uq, numOf(n.then), numOf(n.else)];
    return (c) => (hasUq(c.h, id) ? t(c) : e(c));
  }
  if ('form' in n) {
    const [id, bit, t, e] = [n.form, FORM_BIT[n.key], numOf(n.then), numOf(n.else)];
    return (c) => ((formBits(c.h, id) & bit) !== 0 ? t(c) : e(c));
  }
  if ('formBits' in n) {
    const id = n.formBits;
    return (c) => formBits(c.h, id);
  }
  if ('uqBp' in n) {
    const key = n.uqBp;
    return (c) => uqBp(c.h, c.sk.id, key);
  }
  if ('add' in n) {
    const [a, b] = [numOf(n.add[0]), numOf(n.add[1])];
    return (c) => a(c) + b(c);
  }
  if ('sub' in n) {
    const [a, b] = [numOf(n.sub[0]), numOf(n.sub[1])];
    return (c) => a(c) - b(c);
  }
  if ('times' in n) {
    const [a, b] = [numOf(n.times[0]), numOf(n.times[1])];
    return (c) => a(c) * b(c);
  }
  if ('div' in n) {
    const [a, b] = [numOf(n.div[0]), numOf(n.div[1])];
    return (c) => idiv(a(c), b(c));
  }
  if ('min' in n) {
    const [a, b] = [numOf(n.min[0]), numOf(n.min[1])];
    return (c) => imin(a(c), b(c));
  }
  if ('max' in n) {
    const [a, b] = [numOf(n.max[0]), numOf(n.max[1])];
    return (c) => imax(a(c), b(c));
  }
  if ('ifLess' in n) {
    const [a, b, t, e] = [numOf(n.ifLess[0]), numOf(n.ifLess[1]), numOf(n.then), numOf(n.else)];
    return (c) => (a(c) < b(c) ? t(c) : e(c));
  }
  if ('pres' in n) {
    const v = numOf(n.pres);
    return (c) => presence(c.h, v(c));
  }
  if ('struct' in n) {
    const v = numOf(n.struct);
    return (c) => structural(c.h, v(c));
  }
  if ('heal' in n) {
    const v = numOf(n.heal);
    return (c) => healPower(c.h, v(c));
  }
  if ('healBp' in n) {
    const v = numOf(n.healBp);
    return (c) => mulBp(v(c), c.h.stats.healBp ?? 10000);
  }
  if ('powBp' in n) {
    const v = numOf(n.powBp);
    return (c) => mulBp(v(c), c.h.stats.powerBp);
  }
  if ('inked' in n) {
    const [t, e] = [numOf(n.inked), numOf(n.else)];
    return (c) => ((c.o as Enemy).tier >= 2 ? t(c) : e(c));
  }
  if ('hurtBp' in n) return (c) => hurtBp(c.h);
  const [who, v] = [heroOf(n.of), numOf(n.maxHp)];
  return (c) => mulBp(who(c).stats.maxHp, v(c));
};

/** A step's condition, as a function of the cast. */
const whenOf = (w: When): F<boolean> => {
  if (Array.isArray(w)) {
    const all = (w as readonly When[]).map(whenOf);
    return (c) => {
      for (const f of all) if (!f(c)) return false;
      return true;
    };
  }
  const o = w as Exclude<When, readonly When[]>;
  if ('fork' in o) {
    const f = o.fork;
    return (c) => c.fork === f;
  }
  if ('notFork' in o) {
    const f = o.notFork;
    return (c) => c.fork !== f;
  }
  if ('uq' in o) {
    const id = o.uq;
    return (c) => hasUq(c.h, id);
  }
  if ('notUq' in o) {
    const id = o.notUq;
    return (c) => !hasUq(c.h, id);
  }
  if ('form' in o) {
    const [id, bit] = [o.form, FORM_BIT[o.key]];
    return (c) => (formBits(c.h, id) & bit) !== 0;
  }
  if ('notForm' in o) {
    const [id, bit] = [o.notForm, FORM_BIT[o.key]];
    return (c) => (formBits(c.h, id) & bit) === 0;
  }
  if ('anyForm' in o) {
    const id = o.anyForm;
    return (c) => formBits(c.h, id) !== 0;
  }
  if ('has' in o) {
    const f = FIELD[o.has];
    return (c) => f(c) !== 0;
  }
  if ('at' in o) {
    const p = placeOf(o.at);
    return (c) => p(c) !== null;
  }
  if ('alive' in o) return o.alive === 'e' ? (c) => (c.e as Enemy).hp > 0 : (c) => (c.o as Enemy).hp > 0;
  if ('lowHp' in o) {
    const bp = o.lowHp;
    return (c) => {
      const e = c.e as Enemy;
      return e.hp * 10000 < e.maxHp * bp;
    };
  }
  if ('positive' in o) {
    const k = o.positive;
    return (c) => (c.vars[k] ?? 0) > 0;
  }
  const k = o.some;
  return (c) => (c.foes[k]?.length ?? 0) > 0;
};

type LayFn = (c: Cast, spot: Pt | undefined) => unknown;

/** A value laid on something the cast leaves (OMIT: it isn't there at all), as a function of the cast. */
const layOf = (v: Lay): LayFn => {
  if (v === null || typeof v === 'boolean') return () => v;
  if (typeof v === 'number') return () => v;
  if (typeof v === 'string') {
    if (SKILL_FIELDS.has(v)) {
      const f = FIELD[v as SkillField];
      return (c) => f(c);
    }
    return () => v;
  }
  if ('when' in v) {
    const [w, x] = [whenOf(v.when), layOf(v.v)];
    return (c, spot) => (w(c) ? x(c, spot) : OMIT);
  }
  if ('hid' in v) return (c) => c.h.id;
  if ('eid' in v) return (c) => (c.e as Enemy).id;
  if ('oid' in v) return (c) => (c.o as Enemy).id;
  if ('pid' in v) return (c) => c.h.pid;
  if ('after' in v) {
    const n = numOf(v.after);
    return (c) => c.s.tick + n(c);
  }
  if ('is' in v) {
    const w = whenOf(v.is);
    return (c) => w(c);
  }
  if ('facing' in v) {
    if (v.facing === 'out') return (_c, q) => iatan2(-(q as Pt).y, -(q as Pt).x);
    if (v.facing === 'fromH') return (c, q) => iatan2((q as Pt).y - c.h.y, (q as Pt).x - c.h.x);
    return (c) => c.h.facing;
  }
  if ('cells' in v) return (c) => c.cells ?? [];
  if ('rec' in v) {
    const parts: [string, F<unknown>][] = Object.entries(v.rec).map(([k, x]) => [k, typeof x === 'object' && 'pid' in x ? (c: Cast) => c.h.pid : numOf(x as Num)]);
    return (c) => {
      const r: Record<string, unknown> = {};
      for (const [k, f] of parts) r[k] = f(c);
      return r;
    };
  }
  const n = numOf(v as Num);
  return (c) => n(c);
};

/** Lays every field of a record (those that are there) on what the cast leaves. */
const fieldsOf = (fields: Record<string, Lay>): ((c: Cast, into: Record<string, unknown>, spot: Pt | undefined) => void) => {
  const parts: [string, LayFn][] = Object.entries(fields).map(([k, v]) => [k, layOf(v)]);
  return (c, into, spot) => {
    for (const [k, f] of parts) {
      const v = f(c, spot);
      if (v !== OMIT) into[k] = v;
    }
  };
};

/** Which nightmares a selection keeps, as one function. */
const pickOf = (pick: readonly string[]): ((c: Cast, o: Enemy) => boolean) => {
  const t = pick.includes('t');
  const wall = pick.includes('wall');
  const ground = pick.includes('ground');
  const bad = pick.includes('corrupt');
  const notE = pick.includes('notE');
  return (c, o) => !(notE && o.id === (c.e as Enemy).id) && !(wall && o.kind === 'boneWall') && !(t && !targetable(c.s, o)) && !(ground && isFlying(o)) && !(bad && !corrupt(o));
};

const foeOf = (step: Step): F<Enemy> => (step.on === 'e' ? (c) => c.e as Enemy : (c) => c.o as Enemy);

const stepsOf = (steps: readonly Step[]): F<Flow> => {
  const all = steps.map(stepOf);
  return (c) => {
    for (const f of all) {
      const r = f(c);
      if (r !== GO) return r;
    }
    return GO;
  };
};

const stepOf = (step: Step): F<Flow> => {
  const body = bodyOf(step);
  if (step.when === undefined) return body;
  const w = whenOf(step.when);
  return (c) => (w(c) ? body(c) : GO);
};

const bodyOf = (step: Step): F<Flow> => {
  switch (step.k) {
    case 'enemy':
      return (c) => {
        const { s, h, sk } = c;
        const e = c.cmd.id ? findEnemy(s, c.cmd.id) : null;
        c.e = e && targetable(s, e) && withinRange(h.x, h.y, e.x, e.y, sk.castRange) ? e : nearestEnemy(s, h.x, h.y, sk.castRange);
        return c.e ? GO : FAIL;
      };
    case 'enemyNear': {
      const reach = step.reach === undefined ? null : numOf(step.reach);
      return (c) => {
        const { s, h, sk } = c;
        let e = c.cmd.id ? findEnemy(s, c.cmd.id) : null;
        if (!e || !targetable(s, e) || !withinRange(h.x, h.y, e.x, e.y, sk.castRange)) {
          const p = c.cmd.x !== undefined ? clampPoint(h, c.px, c.py, sk.castRange) : { x: h.x, y: h.y };
          e = nearestEnemy(s, p.x, p.y, c.cmd.x !== undefined ? 1500 : sk.castRange);
        }
        if (!e || (reach && !withinRange(h.x, h.y, e.x, e.y, reach(c)))) return FAIL;
        c.e = e;
        return GO;
      };
    }
    case 'ally': {
      const notSelf = !!step.notSelf;
      const self = !!step.self;
      return (c) => {
        const { s, h, sk } = c;
        let t = c.cmd.id ? findHero(s, c.cmd.id) : null;
        // (By the cleric's stretcher a heal with nobody hurt lands on the cleric: it reaches Tessa, battle/litter.ts.)
        if (!t || t.downTicks > 0 || (notSelf && t.id === h.id) || !withinRange(h.x, h.y, t.x, t.y, sk.castRange)) t = mostHurt(s, h.x, h.y, sk.castRange, notSelf ? h.id : 0) ?? (self || (!notSelf && s.rules?.senda?.litter) ? h : null);
        if (!t || (notSelf && t.id === h.id)) return FAIL;
        c.a = t;
        return GO;
      };
    }
    case 'hurt': {
      const r = numOf(step.r);
      return (c) => {
        c.a = mostHurt(c.s, c.h.x, c.h.y, r(c));
        return GO;
      };
    }
    case 'point': {
      const from = step.from ? ptOf(step.from) : null;
      const range = step.range === undefined ? FIELD.castRange : numOf(step.range);
      const ground = !!step.ground;
      return (c) => {
        const f = from ? from(c) : { x: c.px, y: c.py };
        const p = clampPoint(c.h, f.x, f.y, range(c));
        c.p = p;
        return ground && !heroWalkable(c.s, cellAtWorld(c.s, p.x, p.y)) ? FAIL : GO;
      };
    }
    case 'land': {
      const from = ptOf(step.at ?? 'p');
      const dx = step.dx ?? 0;
      return (c) => {
        const o = from(c);
        c.land = landing(c.s, o.x + dx, o.y);
        return c.land ? GO : FAIL;
      };
    }
    case 'aim':
      return (c) => {
        c.aim = { x: c.px === c.h.x && c.py === c.h.y ? c.h.x + 1 : c.px, y: c.py };
        return GO;
      };
    case 'end': {
      const [from, range] = [ptOf(step.from), numOf(step.range)];
      return (c) => {
        const o = from(c);
        c.end = along(c.h.x, c.h.y, o.x, o.y, range(c));
        return GO;
      };
    }
    case 'face': {
      const to = ptOf(step.to);
      return (c) => {
        const o = to(c);
        c.h.facing = iatan2(o.y - c.h.y, o.x - c.h.x);
        return GO;
      };
    }
    case 'need': {
      const k = step.of;
      return (c) => ((c.foes[k]?.length ?? 0) > 0 ? GO : FAIL);
    }
    case 'near': {
      const [from, r] = [ptOf(step.at), numOf(step.r)];
      const pick = step.pick ? pickOf(step.pick) : null;
      const nearest = step.nearest === undefined ? null : numOf(step.nearest);
      const into = step.into ?? 'hits';
      const withE = !!step.withE;
      return (c) => {
        const o = from(c);
        let xs = enemiesNear(c.s, o.x, o.y, r(c), pick ? (x) => pick(c, x) : undefined);
        if (nearest) xs = xs.sort((a, b) => dist2(a.x, a.y, o.x, o.y) - dist2(b.x, b.y, o.x, o.y) || a.id - b.id).slice(0, nearest(c));
        c.foes[into] = withE ? [c.e as Enemy, ...xs] : xs;
        return GO;
      };
    }
    case 'one': {
      const into = step.into ?? 'hits';
      return step.of === 'e'
        ? (c) => {
            c.foes[into] = [c.e as Enemy];
            return GO;
          }
        : (c) => {
            c.friends[into] = [c.a as Hero];
            return GO;
          };
    }
    case 'cone': {
      const [r, deg] = [numOf(step.r), numOf(step.deg)];
      const into = step.into ?? 'hits';
      return (c) => {
        const o = c.aim as Pt;
        c.foes[into] = enemiesInCone(c.s, c.h, o.x, o.y, r(c), deg(c));
        return GO;
      };
    }
    case 'line': {
      const [to, w] = [ptOf(step.to), numOf(step.w)];
      const into = step.into ?? 'hits';
      return (c) => {
        const o = to(c);
        c.foes[into] = enemiesInLine(c.s, c.h.x, c.h.y, o.x, o.y, w(c));
        return GO;
      };
    }
    case 'allies': {
      const [from, rr] = [ptOf(step.at), numOf(step.r)];
      const notSelf = !!step.notSelf;
      const into = step.into ?? 'hits';
      return (c) => {
        const o = from(c);
        const r = rr(c);
        const h = c.h;
        c.friends[into] = c.s.heroes.filter((x) => (!notSelf || x.id !== h.id) && x.downTicks === 0 && withinRange(o.x, o.y, x.x, x.y, r));
        return GO;
      };
    }
    case 'allyLine': {
      const [to, w] = [ptOf(step.to), numOf(step.w)];
      const notSelf = !!step.notSelf;
      const into = step.into ?? 'hits';
      return (c) => {
        const o = to(c);
        const h = c.h;
        const xs = heroesInLine(c.s, h.x, h.y, o.x, o.y, w(c));
        c.friends[into] = notSelf ? xs.filter((x) => x.id !== h.id) : xs;
        return GO;
      };
    }
    case 'buildings': {
      const [from, rr] = [ptOf(step.at), numOf(step.r)];
      const into = step.into ?? 'hits';
      return (c) => {
        const o = from(c);
        const r = rr(c);
        c.counted[into] = c.s.structures.filter((st) => !st.destroyed && withinRange(o.x, o.y, hexToWorld(st.q, st.r).x, hexToWorld(st.q, st.r).y, r)).length;
        return GO;
      };
    }
    case 'each': {
      const k = step.of;
      const body = stepsOf(step.do);
      return (c) => {
        const enemies = c.foes[k];
        if (enemies) {
          for (const o of enemies) {
            c.o = o;
            const f = body(c);
            if (f === FAIL || f === STOP) return f;
          }
          c.o = null;
          return GO;
        }
        for (const o of c.friends[k] ?? []) {
          c.ho = o;
          const f = body(c);
          if (f === FAIL || f === STOP) return f;
        }
        c.ho = null;
        return GO;
      };
    }
    case 'dmg': {
      const e = foeOf(step);
      const amt = numOf(step.amt);
      const forkType = typeof step.type === 'object' ? step.type : null;
      const fixed = typeof step.type === 'string' && step.type !== 'physical' ? step.type : undefined;
      const ignore = step.armor === 'ignore';
      const pierce = step.armor === 'pierce';
      const noReact = !!step.noReact;
      const steal = !!step.steal;
      const times = step.times ?? 1;
      return (c) => {
        const t = c.conv ?? (forkType ? ((c.fork ? forkType.fork[c.fork] : undefined) ?? forkType.else) : fixed);
        const target = e(c);
        for (let i = 0; i < times; i++) {
          // A fresh set of options each blow: damageEnemy may write on them.
          const o: { pid: string; ignoresArmor?: boolean; pierce?: boolean; type?: DamageType; noReact?: boolean } = { pid: c.h.pid };
          if (ignore) o.ignoresArmor = true;
          if (pierce) o.pierce = true;
          if (t !== undefined && t !== 'physical') o.type = t;
          if (noReact) o.noReact = true;
          // Worked out again each blow: a barbarian's fury grows with what the last one took from it.
          const dealt = damageEnemy(c.s, target, amt(c), o);
          if (steal) lifesteal(c.s, c.h, dealt);
        }
        return GO;
      };
    }
    case 'smite': {
      const [e, amt] = [foeOf(step), numOf(step.amt)];
      return (c) => {
        c.vars.dealt = (c.vars.dealt ?? 0) + smiteOne(c.s, c.h, e(c), amt(c), c.conv ?? 'holy');
        return GO;
      };
    }
    case 'stun': {
      const [e, tt] = [foeOf(step), numOf(step.t)];
      const heavy = !!step.heavy;
      return (c) => {
        const target = e(c);
        const t = tt(c);
        // On the dragon a stun turned to frost or fire is no dry blow (docs/UNICAS.md §4.3): it doesn't land at all.
        const st = target.kind === 'dragon' && (c.conv === 'frost' || c.conv === 'fire') ? 0 : heavy ? heavyStun(target, t) : stunFor(target, t);
        if (target.stun < st) target.stun = st;
        return GO;
      };
    }
    case 'slow': {
      const [e, bp, t] = [foeOf(step), numOf(step.bp), numOf(step.t)];
      return (c) => {
        slowIt(e(c), bp(c), t(c));
        return GO;
      };
    }
    case 'ground': {
      const [e, t] = [foeOf(step), numOf(step.t)];
      return (c) => {
        const target = e(c);
        // (Nothing brings the dragon of the Burn down but its harpoons.)
        if ((isFlying(target) || (target.grounded ?? 0) > 0) && target.kind !== 'dragon') target.grounded = t(c);
        return GO;
      };
    }
    case 'dot': {
      const [e, dmg, every, t] = [foeOf(step), numOf(step.dmg), numOf(step.every), numOf(step.t)];
      const type = step.type;
      return (c) => {
        const target = e(c);
        target.dotDmg = dmg(c);
        target.dotType = c.conv ?? type;
        target.dotEvery = every(c);
        target.dotTicks = t(c);
        target.lastPid = c.h.pid;
        return GO;
      };
    }
    case 'poison': {
      const [e, dmg, every, t] = [foeOf(step), numOf(step.dmg), numOf(step.every), numOf(step.t)];
      return (c) => {
        poison(e(c), dmg(c), every(c), t(c), c.h.pid, c.conv ?? 'poison');
        return GO;
      };
    }
    case 'sting': {
      const [e, dmg, every, t, jump] = [foeOf(step), numOf(step.dmg), numOf(step.every), numOf(step.t), whenOf(step.jump)];
      return (c) => {
        sting(e(c), dmg(c), every(c), t(c), c.h.pid, jump(c), c.conv ?? 'poison');
        return GO;
      };
    }
    case 'mark': {
      const [e, bb, t] = [foeOf(step), numOf(step.bp), numOf(step.t)];
      return (c) => {
        const target = e(c);
        const bp = bb(c);
        if (!target.markTicks || target.markTicks <= 0 || (target.markBp ?? 0) <= bp) {
          target.markBp = bp;
          target.markTicks = t(c);
        }
        return GO;
      };
    }
    case 'blind': {
      const [e, tt, bp] = [foeOf(step), numOf(step.t), numOf(step.bp)];
      return (c) => {
        const target = e(c);
        const t = tt(c);
        if ((target.blind ?? 0) < t) target.blind = t;
        target.blindBp = bp(c);
        return GO;
      };
    }
    case 'taunt': {
      const [e, t] = [foeOf(step), numOf(step.t)];
      return (c) => {
        taunt(e(c), c.h, t(c));
        return GO;
      };
    }
    case 'shove': {
      const [e, d] = [foeOf(step), numOf(step.d)];
      return (c) => {
        shove(c.s, e(c), c.h.x, c.h.y, d(c));
        return GO;
      };
    }
    case 'acid': {
      const [e, tt] = [foeOf(step), numOf(step.t)];
      return (c) => {
        const target = e(c);
        const t = tt(c);
        if ((target.acidTicks ?? 0) < t) target.acidTicks = t;
        return GO;
      };
    }
    case 'skipDead':
      return (c) => ((c.o as Enemy).hp <= 0 ? SKIP : GO);
    case 'killed':
      return (c) => {
        if ((c.o as Enemy).hp <= 0) c.killed = true;
        return GO;
      };
    case 'set': {
      const to = step.to;
      const who: F<Record<string, unknown>> =
        to === 'e' ? (c) => c.e as unknown as Record<string, unknown> : to === 'o' ? (c) => c.o as unknown as Record<string, unknown> : (heroOf(to) as unknown as F<Record<string, unknown>>);
      const v = layOf(step.v);
      const f = step.f;
      const up = !!step.up;
      return (c) => {
        const laid = v(c, undefined);
        if (laid === OMIT) return GO;
        const o = who(c);
        // Null clears the field.
        const x = laid === null ? undefined : laid;
        if (up) {
          if (((o[f] as number | undefined) ?? 0) < (x as number)) o[f] = x;
        } else o[f] = x;
        return GO;
      };
    }
    case 'boom': {
      const [from, r] = [ptOf(step.at), numOf(step.r)];
      const kinds = step.kind;
      const kind: (c: Cast) => string = typeof kinds === 'string' ? () => kinds : (c) => (c.fork ? kinds.fork[c.fork] : undefined) ?? kinds.else;
      return (c) => {
        const o = from(c);
        ev(c.s, { t: 'boom', x: o.x, y: o.y, r: r(c), kind: kind(c) });
        return GO;
      };
    }
    case 'beam': {
      const [from, to] = [ptOf(step.from), ptOf(step.to)];
      const kind = step.kind;
      return (c) => {
        const a = from(c);
        const b = to(c);
        ev(c.s, { t: 'beam', x: a.x, y: a.y, tx: b.x, ty: b.y, kind });
        return GO;
      };
    }
    case 'shot': {
      const [from, to] = [ptOf(step.from), ptOf(step.to)];
      const kind = step.kind;
      return (c) => {
        const a = from(c) as Enemy;
        const b = to(c) as Enemy;
        ev(c.s, { t: 'shot', from: a.id, to: b.id, kind, x: a.x, y: a.y, tx: b.x, ty: b.y });
        return GO;
      };
    }
    case 'zone': {
      const where = step.at === 'spot' ? null : ptOf(step.at);
      const lay = fieldsOf(step.z);
      return (c) => {
        const { s, h } = c;
        for (const q of where ? [where(c)] : (c.spots ?? [c.p as Pt])) {
          const z = { id: s.nextId++, kind: '', x: q.x, y: q.y, radius: 0, cells: [] as number[], ticks: 0, every: 12, damage: 0, slowBp: 0, pid: h.pid } as Record<string, unknown>;
          lay(c, z, q);
          if (c.conv) z.dt = c.conv;
          s.zones.push(z as unknown as Zone);
        }
        return GO;
      };
    }
    case 'bot': {
      const where = step.at === 'spot' ? null : ptOf(step.at);
      const lay = fieldsOf(step.b);
      return (c) => {
        const { s, h } = c;
        const bots = (s.bots ??= []);
        let last: Record<string, unknown> | null = null;
        for (const q of where ? [where(c)] : (c.spots ?? [c.p as Pt])) {
          const b = { id: s.nextId++, owner: h.id, pid: h.pid, kind: '', x: q.x, y: q.y } as Record<string, unknown>;
          lay(c, b, q);
          if (c.conv) b.dt = c.conv;
          bots.push(b as unknown as Bot);
          last = b;
        }
        // A pack glyph: one more beside the last (its life and damage already cut by the glyph).
        if (c.pack && last) {
          bots.push({ ...last, id: s.nextId++, x: (last.x as number) + 400 } as unknown as Bot);
          c.pack = false;
        }
        return GO;
      };
    }
    case 'launch': {
      const [from, to] = [ptOf(step.from), ptOf(step.to)];
      const lay = fieldsOf(step.pr);
      return (c) => {
        const a = from(c);
        const b = to(c);
        const pr = { kind: '', x: a.x, y: a.y, tx: b.x, ty: b.y } as Record<string, unknown>;
        lay(c, pr, undefined);
        pr.pid = c.h.pid;
        if (c.conv) pr.dt = c.conv;
        launch(c.s, pr as unknown as Parameters<typeof launch>[1]);
        return GO;
      };
    }
    case 'clearZones': {
      const kind = step.kind;
      return step.by === 'pid'
        ? (c) => {
            c.s.zones = c.s.zones.filter((z) => z.kind !== kind || z.pid !== c.h.pid);
            return GO;
          }
        : (c) => {
            c.s.zones = c.s.zones.filter((z) => z.kind !== kind || z.follow !== c.h.id);
            return GO;
          };
    }
    case 'clearBots': {
      const kind = step.kind;
      return (c) => {
        c.s.bots = (c.s.bots ?? []).filter((b) => b.owner !== c.h.id || b.kind !== kind || b.tormo);
        return GO;
      };
    }
    case 'capZones': {
      const kind = step.kind;
      const cap = numOf(step.n);
      return (c) => {
        const { s, h } = c;
        const n = c.spots?.length ?? 1;
        const mine = s.zones.filter((z) => z.kind === kind && z.pid === h.pid);
        const most = cap(c) * n;
        if (mine.length + n > most) {
          const drop = new Set(mine.slice(0, mine.length + n - most).map((z) => z.id));
          s.zones = s.zones.filter((z) => !drop.has(z.id));
        }
        return GO;
      };
    }
    case 'spots': {
      const gg = numOf(step.gap);
      const pattern = step.pattern;
      const count = numOf(step.n ?? 1);
      return (c) => {
        const p = c.p as Pt;
        const gap = gg(c);
        if (pattern === 'pair') c.spots = [-1, 1].map((k) => ({ x: p.x + k * idiv(gap, 2), y: p.y }));
        else if (pattern === 'ring3') c.spots = [0, 341, 682].map((a) => ({ x: p.x + idiv(cosA(a) * gap, 10000), y: p.y + idiv(sinA(a) * gap, 10000) }));
        else {
          const n = count(c);
          c.spots = [];
          for (let i = 0; i < n; i++) {
            const a = idiv(i * 1024, n);
            c.spots.push(n > 1 ? { x: p.x + idiv(cosA(a) * gap, 10000), y: p.y + idiv(sinA(a) * gap, 10000) } : p);
          }
        }
        return GO;
      };
    }
    case 'cells': {
      const [from, n] = [ptOf(step.at), numOf(step.n)];
      return (c) => {
        c.cells = wallCells(c.s, c.h, from(c), n(c));
        return GO;
      };
    }
    case 'move': {
      const to = ptOf(step.to);
      return (c) => {
        const o = to(c);
        const h = c.h;
        h.x = o.x;
        h.y = o.y;
        h.destX = o.x;
        h.destY = o.y;
        h.ax = o.x;
        h.ay = o.y;
        h.path = [];
        h.moving = false;
        return GO;
      };
    }
    case 'heal': {
      const [who, amt] = [heroOf(step.to), numOf(step.amt)];
      return (c) => {
        healHero(c.s, who(c), amt(c), c.h.pid);
        return GO;
      };
    }
    case 'ward': {
      const [who, bp, t] = [heroOf(step.to), numOf(step.bp), numOf(step.t)];
      return (c) => {
        wardHero(who(c), bp(c), t(c));
        return GO;
      };
    }
    case 'inspire': {
      const who = heroOf(step.to);
      return (c) => {
        inspire(who(c));
        return GO;
      };
    }
    case 'taunter': {
      const [t, r] = [numOf(step.t), numOf(step.r)];
      return (c) => {
        c.h.taunt = t(c);
        c.h.tauntRadius = r(c);
        return GO;
      };
    }
    case 'keep': {
      const [k, v] = [step.name, numOf(step.v)];
      return (c) => {
        c.vars[k] = v(c);
        return GO;
      };
    }
    case 'record': {
      const of = step.of;
      const n = of === undefined ? numOf(step.n as Num) : null;
      return (c) => {
        recordArea(c.s, c.h, c.sk.id, n ? n(c) : (c.foes[of as string]?.length ?? c.friends[of as string]?.length ?? c.counted[of as string] ?? 0));
        return GO;
      };
    }
    case 'highlight':
      return (c) => {
        c.s.stats.highlights.push({ tick: c.s.tick, kind: 'oath', pid: c.h.pid });
        return GO;
      };
    case 'stop':
      return () => STOP;
    case 'special': {
      const fn = SPECIALS[step.fn];
      if (!fn) throw new Error(`skill program: no special "${step.fn}"`);
      const n = step.n === undefined ? () => 0 : numOf(step.n);
      return (c) => (fn(c, n(c)) ? GO : FAIL);
    }
  }
};

/** Each skill's program, compiled the first time it is cast. */
const PROGRAMS = new Map<SkillId, F<Flow>>();
const programOf = (id: SkillId): F<Flow> => {
  let p = PROGRAMS.get(id);
  if (!p) {
    p = stepsOf(SKILL_CASTS[id].steps);
    PROGRAMS.set(id, p);
  }
  return p;
};

const freshCast = (s: BattleState, h: Hero, sk: SkillDef, slot: 0 | 1 | 2, cmd: SkillCmd, pack: boolean): Cast => ({
  s,
  h,
  sk,
  fork: h.forks?.[slot] ?? null,
  cmd,
  px: cmd.x ?? h.x,
  py: cmd.y ?? h.y,
  p: null,
  aim: null,
  e: null,
  a: null,
  land: null,
  end: null,
  q: null,
  o: null,
  ho: null,
  g: null,
  foes: {},
  friends: {},
  counted: {},
  vars: {},
  spots: null,
  cells: null,
  killed: false,
  cd: -1,
  conv: glyphTurns(sk.id, h.glyphs?.[slot]),
  pack,
});

/** A fan glyph's side cast: the command turned `turn` (of 1024 to a turn) around the hero, at nothing in particular. */
const turnedCmd = (h: Hero, sk: SkillDef, cmd: SkillCmd, turn: number): SkillCmd => {
  let dx = (cmd.x ?? h.x) - h.x;
  let dy = (cmd.y ?? h.y) - h.y;
  if (dx === 0 && dy === 0) {
    dx = idiv(cosA(h.facing) * sk.castRange, 10000);
    dy = idiv(sinA(h.facing) * sk.castRange, 10000);
  }
  const cs = cosA(turn);
  const sn = sinA(turn);
  return { t: 'skill', slot: cmd.slot, x: h.x + idiv(dx * cs - dy * sn, 10000), y: h.y + idiv(dx * sn + dy * cs, 10000) };
};

/** A trail glyph: ground of the skill's type where it struck (docs/GLIFOS.md §1). */
const trailAfter = (c: Cast): void => {
  const { s, h, sk } = c;
  const form = SKILL_CASTS[sk.id].form;
  const hit = c.foes.hits?.[0] ?? null;
  const at: Pt = form === 'leap' ? (c.land ?? h) : form === 'strike' ? (c.e ?? h) : form === 'circle' ? (c.p ?? h) : (hit ?? c.end ?? c.p ?? h);
  const f = GLYPH_FX.trail;
  s.zones.push({
    id: s.nextId++,
    kind: 'burn',
    x: at.x,
    y: at.y,
    radius: imax(sk.radius, f.radius),
    cells: [],
    ticks: f.ticks,
    every: f.every,
    damage: mulBp(power(h, sk.damage), f.bp),
    slowBp: 0,
    pid: h.pid,
    dt: c.conv ?? SKILL_CASTS[sk.id].type,
  });
};

/** The steps of a self skill that fall on the hero, laid instead on the hero at hand (`ho`): what a bond glyph shares. */
const BONDS = new Map<SkillId, F<Flow>>();
const bondOf = (id: SkillId): F<Flow> => {
  let p = BONDS.get(id);
  if (!p) {
    const steps = SKILL_CASTS[id].steps.flatMap((st): Step[] => {
      if (st.k === 'keep') return [st];
      if ((st.k === 'set' || st.k === 'heal' || st.k === 'ward' || st.k === 'inspire') && st.to === 'h') return [{ ...st, to: 'ho' } as Step];
      return [];
    });
    p = stepsOf(steps);
    BONDS.set(id, p);
  }
  return p;
};

/**
 * A bond glyph (docs/GLIFOS.md §1): what the skill did on the hero (or on the ally it picked) reaches the nearest other
 * ally too, at half its length and power. An ally skill is cast again at that ally; a self one lays its own steps on it.
 */
const bondAfter = (c: Cast, slot: 0 | 1 | 2): void => {
  const { s, h, sk } = c;
  const def = SKILL_CASTS[sk.id];
  const from: Pt = def.form === 'ally' ? (c.a ?? h) : h;
  const reach = def.form === 'ally' ? sk.castRange : GLYPH_FX.bond.reach;
  let to: Hero | null = null;
  for (const o of s.heroes) {
    if (o.id === h.id || o.id === c.a?.id || o.downTicks > 0 || !withinRange(h.x, h.y, o.x, o.y, reach)) continue;
    if (!to || dist2(o.x, o.y, from.x, from.y) < dist2(to.x, to.y, from.x, from.y) || (dist2(o.x, o.y, from.x, from.y) === dist2(to.x, to.y, from.x, from.y) && o.id < to.id)) to = o;
  }
  if (!to) return;
  const half = patched(sk, { duration: { bp: GLYPH_FX.bond.bp }, ...potency(sk.id, GLYPH_FX.bond.bp) });
  ev(s, { t: 'beam', x: from.x, y: from.y, tx: to.x, ty: to.y, kind: 'bond' });
  if (def.form === 'ally') {
    programOf(sk.id)(freshCast(s, h, half, slot, { t: 'skill', slot, x: to.x, y: to.y, id: to.id }, false));
    return;
  }
  const b = freshCast(s, h, half, slot, c.cmd, false);
  b.ho = to;
  bondOf(sk.id)(b);
};

/** A vortex glyph: what stands in the skill's area is dragged toward its middle (not bosses, the Warden or elites). */
const vortexAfter = (c: Cast): void => {
  const { s, h, sk } = c;
  const at: Pt = c.p ?? h;
  for (const e of enemiesNear(s, at.x, at.y, sk.radius, (o) => targetable(s, o))) {
    if (stunFor(e, 1) === 0 || e.kind === 'boneWall') continue;
    const d = isqrt(dist2(e.x, e.y, at.x, at.y));
    if (d <= 300) continue;
    const to = along(e.x, e.y, at.x, at.y, imin(GLYPH_FX.vortex.pull, d - 300));
    if (!isFlying(e) && !enemySlideClear(s, e.x, e.y, to.x, to.y)) continue;
    e.x = to.x;
    e.y = to.y;
    e.next = -1;
  }
  ev(s, { t: 'boom', x: at.x, y: at.y, r: sk.radius, kind: 'vortex' });
};

/**
 * Casts a skill by its program, with the form glyph on its button (docs/GLIFOS.md §1): a fan casts it three times,
 * a trail and a vortex add their own after it, an echo sets it to come again. False when it came to nothing (no one to
 * aim at, no ground to stand on): no cooldown, no event. `echo`: this is that second cast (no cooldown, no event).
 */
export const runCast = (s: BattleState, h: Hero, sk: SkillDef, slot: 0 | 1 | 2, cmd: SkillCmd, echo = false): boolean => {
  const def = SKILL_CASTS[sk.id];
  const g = h.glyphs?.[slot] ?? null;
  const form = g && glyphFits(sk.id, g) ? g : null;
  const c = freshCast(s, h, sk, slot, cmd, form === 'pack');
  if (echo) c.again = true;
  if (programOf(sk.id)(c) === FAIL) return false;
  featCast(s, h, sk.id, c.foes.hits ?? [], c.vars, c.e, c.killed, c.fork, echo, c.g);
  if (form === 'fan')
    for (const turn of [-GLYPH_FX.fan.spread, GLYPH_FX.fan.spread]) {
      const f = freshCast(s, h, sk, slot, turnedCmd(h, sk, cmd, turn), false);
      // A fan's other two casts count what they do for the feats, not as uses of the power.
      if (programOf(sk.id)(f) !== FAIL) featCast(s, h, sk.id, f.foes.hits ?? [], f.vars, f.e, f.killed, f.fork, true, f.g);
    }
  if (form === 'trail') trailAfter(c);
  if (form === 'vortex') vortexAfter(c);
  if (form === 'bond') bondAfter(c, slot);
  if (echo) return true;
  if (form === 'echo') h.echo = { slot, at: s.tick + GLYPH_FX.echo.delay, ...(cmd.x !== undefined ? { x: cmd.x } : {}), ...(cmd.y !== undefined ? { y: cmd.y } : {}), ...(cmd.id !== undefined ? { id: cmd.id } : {}) };
  if (def.end) {
    const t = c.g ?? (placeOf(def.end.at)(c) as Pt);
    ev(s, { t: 'skill', hero: h.id, skill: sk.id, x: t.x, y: t.y });
    h.skillCd[slot] = c.cd >= 0 ? c.cd : mulBp(sk.cooldown, h.stats.cooldownBp);
    h.anim = def.end.anim;
    h.animT = 0;
    pstats(s, h.pid).skills++;
    return true;
  }
  h.skillCd[slot] = c.cd >= 0 ? c.cd : mulBp(mulBp(sk.cooldown, h.stats.cooldownBp), uqBp(h, sk.id, 'cooldownBp'));
  h.anim = slot === 0 ? 'skill0' : slot === 1 ? 'skill1' : 'skill2';
  h.animT = 0;
  pstats(s, h.pid).skills++;
  ev(s, { t: 'skill', hero: h.id, skill: sk.id, x: c.px, y: c.py });
  return true;
};

/** A fallen hero raised by this cast (the oath with the postern medal, dawn): counted for their feats (battle/feats.ts). */
const raised = (c: Cast, o: Hero): void => {
  c.vars.raised = (c.vars.raised ?? 0) + 1;
  if (o.id !== c.h.id && o.control !== 'merc' && !o.pid.startsWith('npc:')) c.vars.raisedPlayers = (c.vars.raisedPlayers ?? 0) + 1;
};

/** Effects only one skill has, by the name its program calls them. False: the cast comes to nothing. */
const SPECIALS: Record<string, (c: Cast, n: number) => boolean> = {
  // ——— The panoply (docs/PANOPLIA.md §10; battle/panoply.ts) ———
  // The druid's (battle/panoply-druid.ts).
  /** The hive staff: the swarm leaves a hive where it struck (`n`: the swarm's poison). */
  hive: ({ s, h, e, sk }, dmg) => {
    hiveOn(s, h, e, dmg, sk.every, sk.duration);
    return true;
  },
  /** The herbalist's staff: the cloud just laid drifts. */
  drift: ({ s, h }) => {
    driftOn(s, h);
    return true;
  },
  /** The briar staff: what the lash drags stays thorned. */
  briarThorn: ({ s, h, o }) => {
    briarThorn(s, h, o);
    return true;
  },
  /** The briar staff: the lash done (its test; Seto). */
  briarHedge: (c) => {
    briarHedge(c.s, c.h, c.foes.hits ?? [], c.aim);
    return true;
  },
  /** The poultice tunic: the rooted ally gives back (`n`: how long). */
  rootBy: ({ s, h, ho }, ticks) => {
    rootBy(s, h, ho, ticks);
    return true;
  },
  /** The goatherd's pelt: the shadow wolves (`n`: how long the form lasts). */
  shadeWolves: ({ s, h }, ticks) => {
    shadeWolves(s, h, ticks);
    return true;
  },
  /** The gardener's plate: the wall just laid holds. */
  wallHold: ({ s, h }) => {
    wallHoldOn(s, h);
    return true;
  },
  /** The undertaker's pendant: the plague just laid passes on. */
  contagion: ({ s, h }) => {
    contagionOn(s, h);
    return true;
  },
  /** The ground stone: the quake's cracks (`n`: its circle). */
  cracks: ({ s, h, p }, radius) => {
    cracksOn(s, h, p, radius);
    return true;
  },
  /** The orchard acorn: the trees just woken hold. */
  treeHold: ({ s, h }) => {
    treeHoldOn(s, h);
    return true;
  },
  /** The stoker's poker: the jet pushes what it burns (`n`: its blow, for the embers). */
  bellows: (c, dmg) => {
    bellows(c.s, c.h, c.foes.hits ?? [], dmg);
    return true;
  },
  /** The clockmaker's gear's final form: a second drone. */
  secondDrone: (c) => {
    secondDrone(c.s, c.h);
    return true;
  },
  /** The storm staff: the bolt comes back, and splits (Rayo partido). */
  boltBack: (c) => {
    boltBack(c.s, c.h, c.foes.chain ?? [], power(c.h, c.sk.damage), c.vars.jumps ?? 0, c.vars.fall ?? 10000, c.sk.radius, c.conv ?? 'lightning');
    return true;
  },
  /** The distiller's staff: the eaten one drips (`n`: the dart's blow). */
  drip: (c, dmg) => {
    dripOn(c.s, c.h, c.e, dmg);
    return true;
  },
  /** The pantry staff: the lance's line stays frozen. */
  iceTrail: (c) => {
    iceTrail(c.s, c.h, c.end ?? c.h, (c.foes.hits ?? []).length);
    return true;
  },
  /** The jailer's surcoat: the binding's deaths are shared. */
  jailLinks: (c) => {
    jailLinks(c.s, c.h, c.foes.hits ?? [], c.sk.duration);
    return true;
  },
  /** The messenger's cape's final form: an image where the mage stood. */
  leftImage: (c) => {
    leftImage(c.s, c.h);
    return true;
  },
  /** The messenger's cape: a flare where it lands (`n`: its blow). */
  arrivalFlare: (c, dmg) => {
    const frost = c.fork === 'frostBlink';
    arrivalFlare(c.s, c.h, dmg, c.sk.radius, c.conv ?? (frost ? 'frost' : 'fire'), frost ? { bp: FORKS.frostBlink.slowBp, ticks: FORKS.frostBlink.slowTicks } : null, (c.foes.hits ?? []).length);
    return true;
  },
  /** The butcher's cleaver: the low blow's mark carries its owner and its jumps. */
  cleaverMark: ({ h, e }) => {
    if (e) lowBlowMarked(h, e);
    return true;
  },
  /** The barracks mace: what the slam pushed knocks into the one behind (`n`: the blow's damage); then, with Dos golpes, again. */
  slamClash: (c, dmg) => {
    slamClash(c.s, c.h, c.foes.hits ?? [], dmg, new Set());
    const aim = c.aim as Pt;
    slamAgainLater(c.s, c.h, aim.x, aim.y, c.vars.r ?? 0, c.vars.deg ?? 0, dmg, c.vars.stun ?? 0, SKILL_FX.shieldSlam.push);
    return true;
  },
  /** The well hook: an elite or a boss chained, the warrior leaps to it (once a cast). */
  hookLeap: (c, dmg) => {
    const done = { leapt: (c.vars.leapt ?? 0) > 0 };
    if (c.o && hookLeap(c.s, c.h, c.o, dmg, SKILL_FX.grapple.gap, done)) c.vars.leapt = 1;
    return true;
  },
  /** The drill plate: the decoy where the warrior was, the warrior on the point (`n`: the decoy's pull). */
  decoySwap: (c, taunt) => {
    const p = c.p as Pt;
    decoySwap(c.s, c.h, p.x, p.y, taunt);
    return true;
  },
  /** The front rank's cuirass: the wall pushes while it holds (`n`: how long). */
  wallPush: (c, ticks) => {
    wallPushOn(c.h, ticks);
    return true;
  },
  /** The crier's cuirass: the shout's courage (`n`: how long). */
  courage: (c, ticks) => {
    courageOn(c.s, c.h, ticks, (c.foes.hits ?? []).length);
    return true;
  },
  /** The mender's thimble: where the banner is thrown (into `q`). */
  bannerSpot: (c) => {
    c.q = bannerSpot(c.s, c.h);
    return true;
  },
  /** …and its landing. */
  bannerLand: (c) => {
    const q = c.q as Pt;
    bannerLands(c.s, c.h, q.x, q.y);
    return true;
  },
  /** The bell nail: the ring comes back (`n`: its damage). */
  echoAgain: (c, dmg) => {
    echoAgainLater(c.s, c.h, c.vars.r ?? 0, c.vars.stun ?? 0, dmg, (c.foes.hits ?? []).filter((o) => o.elite).map((o) => o.id));
    return true;
  },
  /** The veteran's medallion: what the stand takes is gathered (`n`: how long). */
  standGather: (c, ticks) => {
    standGather(c.s, c.h, ticks);
    return true;
  },
  // The barbarian's (battle/panoply-barbarian.ts).
  /** The shepherd's axe: the thrown axe leaps to another (`n`: the throw's blow). */
  axeJump: (c, dmg) => {
    if (c.e) axeJump(c.s, c.h, c.e, dmg);
    return true;
  },
  /** The brawler's axe: what the kick knocked down is marked; felled by the kick, it is ready again (`n`: the kick's push). */
  kickMark: (c, push) => {
    if (c.e && kickMark(c.s, c.h, c.e, push)) c.cd = 0;
    return true;
  },
  /** The carter's axe: the brutal blow mends what the wounds added (`n`: the blow without them; `d`: as it landed). */
  carterHeal: (c, base) => {
    if (c.e) carterHeal(c.s, c.h, c.e, base, c.vars.d ?? base);
    return true;
  },
  /** The bull's tusk: the run back, in the queue (`n`: the charge's blow; `cs` its stun, `cw` its width). */
  chargeBack: (c, dmg) => {
    chargeBack(c.s, c.h, c.foes.hits ?? [], dmg, c.vars.cs ?? 0, c.vars.cw ?? 0);
    return true;
  },
  /** The wolf's skull: what the shout stunned carries the fear. */
  fearMark: (c) => {
    fearMark(c.s, c.h, c.foes.hits ?? []);
    return true;
  },
  /** The foreman's pelt: the roar's echo at its end (`n`: how long). */
  roarEcho: (c, ticks) => {
    roarEcho(c.s, c.h, c.friends.roused ?? [], ticks);
    return true;
  },
  /** The challenger's hide: the duel's chain begins. */
  duelStart: (c) => {
    if (c.e) duelStart(c.h, c.e);
    return true;
  },
  /** The ancestors' pelts: the fury's blows and spirits begin. */
  furyStart: (c) => {
    furyStart(c.h);
    return true;
  },
  // The paladin's (battle/panoply-paladin.ts).
  /** The first round's hammer: the flash goes out behind too (`n`: what it burns; `r`, `deg`, `bt` its reach, width and blinding). */
  backFlash: (c, bd) => {
    backFlash(c.s, c.h, c.aim as Pt, c.vars.r ?? 0, c.vars.deg ?? 0, c.vars.bt ?? 0, bd, c.foes.hits ?? []);
    return true;
  },
  /** The penitence hammer: a kill re-arms the atonement (`n`: how long it waits). */
  atoneChain: (c, ticks) => {
    atoneChainOn(c.h, ticks);
    return true;
  },
  /** The keeper's hammer: the seal is the paladin's, and with Dos sellos there are two. */
  keeperSeal: (c) => {
    if (c.e) keeperSeal(c.s, c.h, c.e);
    return true;
  },
  /** The arms master's cuirass: the hammer flies off at the end (`n`: how long it spins; `hd` its blow). */
  hammerLoose: (c, ticks) => {
    hammerLoose(c.s, c.h, ticks, c.vars.hd ?? 0);
    return true;
  },
  /** The armourer's coat: the blessing's share on the ally nearest the blessed one. */
  blessShare: (c) => {
    if (c.a) blessShare(c.s, c.h, c.a);
    return true;
  },
  /** The stair plates: the landing stuns around the ally; Siempre ahí leaves the intervention ready (`n`: how long it lasts). */
  stairStun: (c, ticks) => {
    if (c.a && stairStun(c.s, c.h, c.a, ticks)) c.cd = 0;
    return true;
  },
  /** The blessed stone: with no building around, the paladin is covered as one (`n`: how long). */
  stoneSelf: (c, ticks) => {
    stoneSelf(c.h, c.counted.covered ?? 0, ticks);
    return true;
  },
  /** The elder's charm: the allies the sacrifice covers, watched until it ends (`n`: how long). */
  sacrificeWatch: (c, ticks) => {
    sacrificeWatch(c.s, c.h, ticks);
    return true;
  },
  /** The prayer bead: the avatar's hammers begin (`n`: how long it lasts). */
  avatarStart: (c, ticks) => {
    avatarStart(c.h, ticks);
    return true;
  },
  // The ranger's (battle/panoply-ranger.ts).
  /** The tracker's bow: the poisoned arrows stack, the third bursts in a cloud. */
  trackerCloud: (c) => {
    trackerArrows(c.s, c.h, c.foes.hits ?? [], c.vars.ticks ?? 0);
    return true;
  },
  /** The pantry bow: arrows on the leap; Salto a tiempo, the wait halved when low. */
  leapArrows: (c) => {
    leapArrows(c.s, c.h);
    const cd = timelyLeap(c.h, c.sk);
    if (cd >= 0) c.cd = cd;
    return true;
  },
  /** The quarry bow: at its end the arrow turns (`n`: the shot's damage). */
  deflect: (c, dmg) => {
    const end = c.end as Pt;
    deflect(c.s, c.h, end.x, end.y, c.sk.castRange, c.sk.radius, dmg, c.foes.hits ?? []);
    return true;
  },
  /** The great ballista's tip: splinters from each elite pierced (`n`: the arrow's damage); Flecha que vuelve. */
  splinters: (c, dmg) => {
    const end = c.end as Pt;
    const from = c.p as Pt;
    splinters(c.s, c.h, c.foes.hits ?? [], dmg, from.x, from.y, end.x, end.y, c.sk.radius);
    return true;
  },
  /** The archer's feather: how much the kills may lengthen the hiding. */
  hiddenGrows: ({ h }) => {
    hiddenGrows(h);
    return true;
  },
  /** The tuner's lens: the eye's kills counted afresh. */
  eyeOpens: ({ h }) => {
    eyeOpens(h);
    return true;
  },
  // The cleric's (battle/panoply-cleric.ts).
  /** The nurse's staff: the mended's life before the mend… */
  nurseBefore: (c) => {
    c.vars.nb = (c.a as Hero).hp;
    return true;
  },
  /** …and after it: on one low, the mend recharges at once. */
  nurse: (c) => {
    const cd = nurseMend(c.s, c.h, c.a as Hero, c.vars.nb ?? 0);
    if (cd >= 0) c.cd = cd;
    return true;
  },
  /** The water carrier's staff: the ray stays (`n`: its damage). */
  rayStays: (c, dmg) => {
    const end = c.end as Pt;
    rayStays(c.s, c.h, end.x, end.y, c.vars.w ?? 0, dmg, c.vars.heal ?? 0, c.foes.hits ?? [], c.friends.mended ?? []);
    return true;
  },
  /** The lamplighter's staff: the lit one dead, the light jumps (`n`: its damage). */
  lightFollows: (c, dmg) => {
    lightFollows(c.s, c.h, c.e, dmg, c.vars.lh ?? 0);
    return true;
  },
  /** The midwife's staff: the renewed keep whose renewal it is. */
  renewedBy: (c) => {
    renewedBy(c.h, c.friends.who ?? [], c.vars.ticks ?? 0);
    return true;
  },
  /** The cistern's stole: what the cleanse takes falls on the nightmares. */
  returnStates: (c) => {
    returnStates(c.s, c.h, c.friends.who ?? [], c.vars.r ?? 0);
    return true;
  },
  /** The pilgrim's smock: light on the nightmare nearest each one prayed for. */
  prayerLights: (c) => {
    prayerLights(c.s, c.h, c.friends.who ?? [], 10000, c.vars.bp ?? 0);
    return true;
  },
  /** The bronze sun: the column walks on (`n`: the judgment's blow). */
  columnWalks: (c, dmg) => {
    const p = c.p as Pt;
    columnWalks(c.s, c.h, p.x, p.y, c.sk.radius, dmg, c.foes.hits ?? []);
    return true;
  },
  /** The infirmary bell: the choir keeps whose it is. */
  choirBy: (c) => {
    choirBy(c.h, c.friends.who ?? [], c.vars.ticks ?? 0);
    return true;
  },
  /** The vigil medal: the intercession's mark keeps whose it is. */
  vigilMark: (c) => {
    vigilMark(c.h, c.a as Hero);
    return true;
  },
  /** Charge: from afar the warrior leaps to the target first (when there is ground to land on). */
  bashCharge: ({ s, h, e }) => {
    const t = e as Enemy;
    if (withinRange(h.x, h.y, t.x, t.y, 1300)) return true;
    const d = isqrt(dist2(h.x, h.y, t.x, t.y));
    const lx = t.x + idiv((h.x - t.x) * 600, d);
    const ly = t.y + idiv((h.y - t.y) * 600, d);
    if (heroWalkable(s, cellAtWorld(s, lx, ly))) {
      ev(s, { t: 'boom', x: h.x, y: h.y, r: 400, kind: 'bash' });
      h.x = lx;
      h.y = ly;
      h.destX = lx;
      h.destY = ly;
      h.ax = lx;
      h.ay = ly;
      h.path = [];
      h.moving = false;
    }
    return true;
  },
  /** The challenge also reaches those striking a wall, gate or tower within `n`: they leave it to come at the warrior. */
  challengeWalls: (c, n) => {
    const hits = c.foes.hits as Enemy[];
    for (const e of enemiesNear(c.s, c.h.x, c.h.y, n, (o) => o.tgtKind === 2 && o.kind !== 'boneWall')) if (!hits.includes(e)) hits.push(e);
    return true;
  },
  /** Second wind: the oath gives back a share (`n`) of the warrior's life. */
  secondWind: ({ h }, n) => {
    h.hp += mulBp(h.stats.maxHp, n);
    if (h.hp > h.stats.maxHp) h.hp = h.stats.maxHp;
    return true;
  },
  /** The postern medal: the oath raises the fallen it reaches, and none of them falls while it lasts. */
  oathRaise: (c) => {
    const { s, h, sk } = c;
    // Its forms: the vow of all reaches everyone, the long vow half as far but twice as long.
    const reach = oathReach(h, sk.radius);
    const holds = oathHolds(h, sk.duration);
    for (const o of s.heroes) {
      if (!withinRange(h.x, h.y, o.x, o.y, reach)) continue;
      // (One the dragon ate is gone: never raised, never counted.)
      if (s.dragon?.eaten.includes(o.pid)) continue;
      if (o.downTicks > 0) {
        raised(c, o);
        getUp(s, o, fx('posternMedal', h).hpBp as number);
        ev(s, { t: 'boom', x: o.x, y: o.y, r: 600, kind: 'revive' });
      }
      if ((o.undying ?? 0) < holds) o.undying = holds;
    }
    return true;
  },
  /** Plants a turret at the point; past the most it may have (one more with the garrison key), the oldest goes. */
  turret: ({ s, h, sk, p, conv }) => {
    // Only the artificer's own skill turrets count (never the portable ones it placed). Overloading has its own command.
    const mine = s.turrets.filter((t) => t.owner === h.id && !t.portable);
    const at = p as Pt;
    if (mine.length >= sk.amount + (hasUq(h, 'garrisonKey') ? (fx('garrisonKey', h).extra as number) + keyExtra(h) : 0)) {
      const oldest = mine[0] as (typeof mine)[number];
      s.turrets = s.turrets.filter((t) => t.id !== oldest.id);
    }
    s.turrets.push({
      id: s.nextId++,
      owner: h.id,
      x: at.x,
      y: at.y,
      cd: 0,
      expires: s.tick + (h.stats.summonBp ? mulBp(sk.duration, 10000 + h.stats.summonBp) : sk.duration),
      damage: power(h, sk.damage),
      range: sk.radius,
      reload: sk.every,
      aim: 0,
      enchant: h.forks?.[0] === 'frostTurret' ? 'frost' : h.forks?.[0] === 'fireTurret' ? 'fire' : null,
      ...(conv ? { dt: conv } : {}),
    });
    return true;
  },
  /** The automaton at the point (the colossus, or a swarm of spiders); one at a time, a new cast sends the old home. */
  automaton: (c) => {
    const { s, h, sk, p, fork, conv } = c;
    const at = p as Pt;
    const bots = (s.bots ??= []);
    // (Tormo, the artificer's legacy, stays: it only kneels while the automaton stands.)
    s.bots = bots.filter((b) => b.owner !== h.id || b.tormo);
    const base = { owner: h.id, pid: h.pid, x: at.x, y: at.y, cd: 0, facing: iatan2(-at.y, -at.x), anim: 'idle' as const, animT: 0, ...(conv ? { dt: conv } : {}) };
    if (fork === 'swarm') {
      const w = FORKS.swarm;
      for (let i = 0; i < w.count; i++) {
        s.bots.push({ ...base, id: s.nextId++, kind: 'spider', x: at.x + (i - 1) * 350, hp: w.hp, maxHp: w.hp, damage: power(h, mulBp(sk.damage, idiv(w.damage * 10000, SKILLS.automaton.damage))), radius: w.radius, every: 0, expires: s.tick + w.ticks, taunt: 0, aura: 0, auraBp: 0 });
      }
    } else {
      const big = fork === 'colossus';
      const hp = mulBp(presence(h, sk.amount), big ? FORKS.colossus.hpBp : 10000);
      s.bots.push({
        ...base,
        id: s.nextId++,
        kind: big ? 'colossus' : 'automaton',
        hp,
        maxHp: hp,
        damage: power(h, big ? mulBp(sk.damage, FORKS.colossus.damageBp) : sk.damage),
        radius: sk.radius,
        every: sk.every,
        // The living anvil: it no longer wears out.
        expires: formFinal(h, 'anvilHarness') ? ANVIL_FOREVER : s.tick + mulBp(mulBp(sk.duration, uqBp(h, 'automaton', 'durationBp')), 10000 + (h.stats.summonBp ?? 0)),
        taunt: big ? mulBp(ARTIFICER.automaton.taunt, FORKS.colossus.tauntBp) : ARTIFICER.automaton.taunt,
        bossUntil: s.tick + (big ? mulBp(ARTIFICER.automaton.bossTicks, FORKS.colossus.bossBp) : ARTIFICER.automaton.bossTicks),
        aura: ARTIFICER.automaton.aura,
        auraBp: ARTIFICER.automaton.auraBp,
        ...(hasUq(h, 'anvilHarness') ? { follow: true, burst: { radius: fx('anvilHarness', h).radius as number, damage: power(h, fx('anvilHarness', h).damage as number) } } : {}),
      } satisfies Bot);
    }
    // A pack glyph: one more beside it.
    if (c.pack) {
      const last = s.bots[s.bots.length - 1] as Bot;
      s.bots.push({ ...last, id: s.nextId++, x: last.x + 400 });
    }
    ev(s, { t: 'boom', x: at.x, y: at.y, r: 900, kind: 'automaton' });
    return true;
  },
  /** The fallen master's staff: more fireballs leap from where it lands to the nearest beyond its blast. */
  fallenStaff: (c) => {
    const { s, h, sk, p, conv } = c;
    const at = p as Pt;
    const f = fx('fallenStaff', h);
    // Its forms: the prong's more and softer leaps, the master's rain leaping again, the wake's fire along each.
    const leaps = staffLeaps(s, h, at, c.foes.hits ?? [], f.chains as number, f.damageBp as number, sk.radius, f.range as number);
    featStaff(s, h, c.foes.hits ?? [], leaps.map((l) => l.to));
    const wake = formA(h, 'fallenStaff');
    for (const l of leaps) {
      const dmg = mulBp(power(h, sk.damage), l.bp);
      launch(s, { kind: 'fireball', x: l.from.x, y: l.from.y, tx: l.to.x, ty: l.to.y, target: 0, speed: 1000, damage: dmg, splash: idiv(sk.radius * 2, 3), ignoresArmor: true, pierce: false, enchant: null, stun: 0, pid: h.pid, hostile: false, ...(conv ? { dt: conv } : {}) });
      if (wake) staffWake(s, h, l.from, l.to, power(h, sk.damage), conv ?? undefined);
    }
    return true;
  },
  /** The volley: `amount` arrows shared out among those caught, round and round. */
  volley: ({ s, h, sk, foes, fork, conv }) => {
    const near = foes.hits as Enemy[];
    // The hunter's bow: its arrows carry the volley's id (they jump on a kill).
    const vol = hunterVolley(s, h);
    for (let i = 0; i < sk.amount; i++) {
      const e = near[i % near.length] as Enemy;
      launch(s, { kind: 'arrow', x: h.x, y: h.y, tx: e.x, ty: e.y, target: e.id, speed: HEROES.ranger.projectileSpeed, damage: power(h, sk.damage), splash: 0, ignoresArmor: false, pierce: true, enchant: fork === 'frostArrows' ? 'frost' : null, stun: 0, pid: h.pid, hostile: false, ...(conv ? { dt: conv } : {}), ...(vol ? { uq: vol } : {}) });
    }
    return true;
  },
  /** The wanderer's cloak: after the net, the ranger leaps back from its target. */
  leapBack: ({ s, h, e }) => {
    // Its forms: the empty cloak stays behind, the lost trail hides the ranger where it lands.
    cloakLeft(s, h);
    leapBack(s, h, (e as Enemy).x, (e as Enemy).y);
    cloakLanded(s, h);
    return true;
  },
  /** The wanderer's double net: a second one at the nearest beyond the first. */
  doubleNet: (c, range) => {
    const { s, h, sk, e } = c;
    const t = e as Enemy;
    const hits = (c.foes.hits ??= []);
    const far = dist2(h.x, h.y, t.x, t.y);
    let best: Enemy | null = null;
    for (const o of enemiesNear(s, t.x, t.y, range, (x) => targetable(s, x) && !hits.includes(x))) {
      const behind = dist2(h.x, h.y, o.x, o.y) >= far;
      const bb = best ? dist2(h.x, h.y, best.x, best.y) >= far : false;
      if (!best || (behind && !bb) || (behind === bb && (dist2(o.x, o.y, t.x, t.y) < dist2(best.x, best.y, t.x, t.y) || (dist2(o.x, o.y, t.x, t.y) === dist2(best.x, best.y, t.x, t.y) && o.id < best.id)))) best = o;
    }
    if (!best) return true;
    hits.push(best);
    launch(s, { kind: 'arrow', x: h.x, y: h.y, tx: best.x, ty: best.y, target: best.id, speed: HEROES.ranger.projectileSpeed, damage: 1, splash: 0, ignoresArmor: true, pierce: false, enchant: null, stun: sk.stun, hardStun: c.fork === 'steelNet', pid: h.pid, hostile: false, ...(c.conv ? { dt: c.conv } : {}) });
    ev(s, { t: 'boom', x: best.x, y: best.y, r: 500, kind: 'doubleNet' });
    return true;
  },
  /** Chain: the light jumps on to the most hurt around the target. */
  mendChain: ({ s, h, a, vars }) => {
    const tgt = a as Hero;
    let skip = tgt.id;
    let from = tgt;
    for (let i = 0; i < FORKS.mendChain.extra; i++) {
      const next = mostHurt(s, from.x, from.y, FORKS.mendChain.radius, skip);
      if (!next || next.id === tgt.id) break;
      healHero(s, next, mulBp(vars.amount as number, FORKS.mendChain.amountBp), h.pid);
      inspire(next);
      ev(s, { t: 'shot', from: from.id, to: next.id, kind: 'mend', x: from.x, y: from.y, tx: next.x, ty: next.y });
      skip = next.id;
      from = next;
    }
    return true;
  },
  /** Sunrise: the fallen around the cleric get up, everyone there is healed and untouchable a moment (the rosary: the whole party). */
  dawn: (c) => {
    const { s, h, sk, fork } = c;
    const rebirth = fork === 'dawnRebirth';
    const shield = rebirth ? mulBp(sk.duration, FORKS.dawnRebirth.invulnBp) : sk.duration;
    let n = 0;
    // The rosary: the whole party (its early dawn only those near, a little farther than the dawn alone).
    const everywhere = hasUq(h, 'healerRosary') && dawnEverywhere(h);
    const reach = hasUq(h, 'healerRosary') ? dawnReach(h, sk.radius) : sk.radius;
    for (const o of s.heroes) {
      if (!everywhere && !withinRange(h.x, h.y, o.x, o.y, reach)) continue;
      if (s.dragon?.eaten.includes(o.pid)) continue;
      const down = o.downTicks > 0;
      if (down) {
        raised(c, o);
        getUp(s, o, rebirth ? 10000 : sk.amount);
        ev(s, { t: 'boom', x: o.x, y: o.y, r: 600, kind: 'revive' });
      } else healHero(s, o, mulBp(o.stats.maxHp, mulBp(sk.amount, h.stats.healBp ?? 10000)), h.pid);
      if (o.invuln < shield) o.invuln = shield;
      if (h.uqForm) dawnTouched(s, h, o, down);
      n++;
    }
    c.vars.n = n;
    return true;
  },
  /** The wall axe: the whirl draws the nightmares around in first. */
  wallAxe: ({ s, h }) => {
    const pulled = whirlPull(s, h);
    featPull(s, h, pulled);
    if (h.uqForm) axeAfter(s, h, pulled);
    return true;
  },
  whirl: (c) => {
    c.vars.n = whirlPulse(c.s, c.h, 10000);
    return true;
  },
  /** A leap that kills is ready again with the tail fang; the double leap's second jump is ready right away. */
  leapAgain: (c) => {
    const { h } = c;
    // The tail fang: a leap that kills is ready again (with its endless leap, one that struck three too).
    if (hasUq(h, 'tailFang') && (h.uqForm ? fangAgain(h, c.killed, c.foes.hits?.length ?? 0) : c.killed)) c.cd = 0;
    if (h.uqForm && hasUq(h, 'tailFang')) fangSeen(c.s, h, c.vars.cres ?? 10000, c.vars.hunger ?? 10000, c.killed, c.foes.hits?.length ?? 0);
    if (c.fork === 'doubleLeap') {
      if (!h.leapAgain) {
        h.leapAgain = true;
        c.cd = FORKS.doubleLeap.gap;
      } else h.leapAgain = false;
    }
    return true;
  },
  /** The aegis on whoever or whatever it goes on (the keep takes half); its variants and the oath plate. */
  aegis: (c) => {
    const { s, h, sk, cmd } = c;
    // The oath plate: the aegis holds more.
    const plate = hasUq(h, 'oathPlate') ? (fx('oathPlate', h).amountBp as number) : 10000;
    const amount = mulBp(presence(h, sk.amount), plate);
    // On a building or the keep, its level doesn't count (buildings don't grow with the hero).
    const onWall = mulBp(structural(h, sk.amount), plate);
    const back = c.fork === 'reprisal';
    const wide = c.fork === 'faithWall';
    const t = guardTarget(s, h, cmd, sk.castRange);
    if (!t) return false;
    // The oath plate's forms keep what it holds (the relay, the steel oath).
    if (h.uqForm && hasUq(h, 'oathPlate')) aegisCast(h, amount);
    if (t.kind === 'keep') {
      s.keepShield = imax(s.keepShield ?? 0, idiv(onWall, 2));
      s.keepShieldTicks = imax(s.keepShieldTicks ?? 0, sk.duration);
    } else raiseShield(t.o as Hero | Structure, t.kind === 'hero' ? amount : onWall, sk.duration, back);
    // The oath plate: the paladin is covered too, and taunts when its own aegis breaks.
    if (hasUq(h, 'oathPlate')) {
      if (t.o !== h) raiseShield(h, amount, sk.duration, back);
      h.shieldTaunt = true;
    }
    ev(s, { t: 'boom', x: t.x, y: t.y, r: 600, kind: 'aegis' });
    // Wall of faith: the allies and buildings around are covered too, for half.
    if (wide) {
      const half = mulBp(amount, FORKS.faithWall.amountBp);
      for (const o of s.heroes) if (o !== t.o && o.downTicks === 0 && withinRange(t.x, t.y, o.x, o.y, FORKS.faithWall.radius)) raiseShield(o, half, sk.duration, back);
      for (const st of s.structures) {
        if (st === t.o || st.destroyed || st.maxHp <= 0) continue;
        const p = structurePos(st);
        if (withinRange(t.x, t.y, p.x, p.y, FORKS.faithWall.radius)) raiseShield(st, mulBp(onWall, FORKS.faithWall.amountBp), sk.duration, back);
      }
    }
    c.g = { x: t.x, y: t.y };
    return true;
  },
  /** The old root staff: the roots spread to whatever touches the held. */
  rootSpread: (c) => {
    const spread: Enemy[] = [];
    c.vars.spread = rootSpread(c.s, c.foes.hits as Enemy[], c.vars.held as number, c.h, spread);
    c.foes.spread = spread;
    return true;
  },
  /** The old root staff's forms on all it holds (the cast's and the spread's). */
  rootsHeld: (c) => {
    rootsHeld(c.s, c.h, [...(c.foes.hits ?? []), ...(c.foes.spread ?? [])], c.vars.held as number, power(c.h, c.sk.damage));
    return true;
  },
  bear: (c) => {
    shiftBear(c.s, c.h, c.vars.bear as number);
    bearBegan(c.h);
    return true;
  },
  /** The tail fang's chain, for the crescendo's blow and the hunger's heal. */
  fangChain: (c) => {
    c.vars.cres = fangBlowBp(c.h);
    c.vars.hunger = fangHealBp(c.h);
    return true;
  },
  /** The gate blade's forms on the bash's hits: the wave, the rift, the gate shut. */
  gateWave: (c) => {
    gateWave(c.s, c.h, c.foes.hits ?? [], c.sk.stun, c.e as Enemy);
    return true;
  },
  gateRift: (c) => {
    gateRift(c.s, c.h, c.e as Enemy, c.foes.hits ?? [], c.sk.stun);
    return true;
  },
  gateShut: (c) => {
    gateShut(c.s, c.foes.hits ?? []);
    return true;
  },
  /** Ser Gaspar's hammer's forms on the smitten: the toll, the heavy hammer, the guard. */
  smiteToll: (c) => {
    smiteToll(c.s, c.h, c.e as Enemy);
    return true;
  },
  smiteHeavy: (c) => {
    smiteHeavy(c.s, c.e as Enemy);
    return true;
  },
  gasparGuard: (c) => {
    gasparGuard(c.s, c.h, c.e as Enemy, power(c.h, c.sk.damage));
    return true;
  },
  /** The last watch's parapet, while the challenge lasts. */
  parapet: (c) => {
    parapet(c.s, c.h, c.sk.duration);
    return true;
  },
  /** Maelis's kit's pair and flasks: where the grenades fall. */
  kitSpots: (c) => {
    const spots = kitSpots(c.h, c.p as Pt, !c.again);
    if (spots) {
      c.spots = spots;
      if (spots.length === 3) ev(c.s, { t: 'boom', x: (c.p as Pt).x, y: (c.p as Pt).y, r: 1200, kind: 'flasks' });
    }
    return true;
  },
  /** The frost orb's path: steps of `n` from the hero to the point. */
  orbPath: (c, step) => {
    const { h } = c;
    const p = c.p as Pt;
    const dx = p.x - h.x;
    const dy = p.y - h.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    c.vars.steps = imax(1, idiv(d + step - 1, step));
    c.vars.vx = idiv(dx * step, d);
    c.vars.vy = idiv(dy * step, d);
    return true;
  },
  /** Lightning from one nightmare to the next (`jumps`, each `fall` of the one before); it ends at `q`. */
  chain: (c) => {
    const { s, h, sk } = c;
    let dmg = power(h, sk.damage);
    const hit = new Set<number>();
    const chain: Enemy[] = [];
    let cur: Enemy | null = c.e;
    let from = { x: h.x, y: h.y };
    const jumps = c.vars.jumps as number;
    for (let i = 0; i < jumps && cur; i++) {
      if (!hit.has(cur.id)) chain.push(cur);
      hit.add(cur.id);
      damageEnemy(s, cur, dmg, c.conv === 'physical' ? { pid: h.pid } : { pid: h.pid, type: c.conv ?? 'lightning' });
      ev(s, { t: 'beam', x: from.x, y: from.y, tx: cur.x, ty: cur.y, kind: 'chain' });
      from = { x: cur.x, y: cur.y };
      dmg = mulBp(dmg, c.vars.fall as number);
      const at: Enemy = cur;
      cur = enemiesNear(s, at.x, at.y, sk.radius, (o) => targetable(s, o) && !hit.has(o.id))[0] ?? null;
    }
    c.q = from;
    c.foes.chain = chain;
    return true;
  },
  /** Mends the most hurt buildings around the point (`count` of them, the rest at `n` of `amount`); none hurt, nothing to do. */
  tuneUp: (c, rest) => {
    const { s, sk } = c;
    const p = c.p as Pt;
    const hurt = s.structures
      .filter((st) => {
        if (st.destroyed || st.maxHp <= 0 || st.hp >= st.maxHp) return false;
        const w = hexToWorld(st.q, st.r);
        return withinRange(p.x, p.y, w.x, w.y, sk.radius);
      })
      .sort((a, b) => idiv(a.hp * 10000, a.maxHp) - idiv(b.hp * 10000, b.maxHp) || a.id - b.id);
    // The builder's square (docs/PANOPLIA.md §10): with nothing hurt in reach, a shield on the nearest ally.
    if (!hurt.length) return squareShield(s, c.h);
    const amount = c.vars.amount as number;
    hurt.slice(0, c.vars.count as number).forEach((st, i) => {
      const was = st.hp;
      st.hp = imin(st.maxHp, st.hp + (i === 0 ? amount : mulBp(amount, rest)));
      squareTuned(s, c.h, st, st.hp - was, was);
      const w = hexToWorld(st.q, st.r);
      ev(s, { t: 'boom', x: w.x, y: w.y, r: 600, kind: 'tuneUp' });
    });
    return true;
  },
  /** The chain drags its catch to `n` from the warrior (bosses, the Warden and elites aren't dragged; flyers are pulled down). */
  grapplePull: ({ s, h, o }, gap) => {
    const e = o as Enemy;
    if (stunFor(e, 1) === 0 || e.kind === 'boneWall') return true;
    const to = along(h.x, h.y, e.x, e.y, gap);
    if (!isFlying(e) && !enemySlideClear(s, e.x, e.y, to.x, to.y)) return true;
    e.x = to.x;
    e.y = to.y;
    e.next = -1;
    return true;
  },
  /** The briars drag their catch `n` towards the druid (never closer than arm's length, never into a wall). */
  briarPull: ({ s, h, o }, pull) => {
    const e = o as Enemy;
    if (stunFor(e, 1) === 0 || e.kind === 'boneWall') return true;
    const d = isqrt(dist2(e.x, e.y, h.x, h.y));
    if (d <= 900) return true;
    const to = along(e.x, e.y, h.x, h.y, imin(pull, d - 900));
    if (!isFlying(e) && !enemySlideClear(s, e.x, e.y, to.x, to.y)) return true;
    e.x = to.x;
    e.y = to.y;
    e.next = -1;
    return true;
  },
  /** Eagle eye widens the ranger's reach (once: a recast only renews it). */
  eagleRange: ({ h, sk }) => {
    if (!h.eagle || !h.eagleBase) {
      h.eagleBase = h.stats.range;
      h.stats.range = mulBp(h.stats.range, sk.amount);
    }
    return true;
  },
  /** Shadow chains: those bound share what one of them takes (`share`). */
  links: (c) => {
    const { s, sk } = c;
    const e = c.e as Enemy;
    const id = s.nextId++;
    for (const o of c.foes.hits as Enemy[]) {
      o.linkId = id;
      o.linkTicks = sk.duration;
      o.linkBp = c.vars.share as number;
      if (o.id !== e.id) ev(s, { t: 'beam', x: e.x, y: e.y, tx: o.x, ty: o.y, kind: 'shadowChain' });
    }
    return true;
  },
  /** Where the ranger leaps away to: `n` from the nearest nightmare, straight back (or behind it, with none around). */
  awayPoint: (c, range) => {
    const { s, h } = c;
    const near = nearestEnemy(s, h.x, h.y, 4000);
    const away = near ? { x: h.x * 2 - near.x, y: h.y * 2 - near.y } : { x: h.x - idiv(cosA(h.facing) * 1000, 10000), y: h.y - idiv(sinA(h.facing) * 1000, 10000) };
    c.p = along(h.x, h.y, away.x === h.x && away.y === h.y ? h.x + 1 : away.x, away.y, range);
    return true;
  },
  /** The duel's foe: an elite or a boss in reach if there's one (the biggest); otherwise the one chosen, or the nearest. */
  duelPick: (c) => {
    const { s, h, sk } = c;
    let e: Enemy | null = null;
    for (const o of enemiesNear(s, h.x, h.y, sk.castRange, (x) => targetable(s, x) && (x.elite || isBoss(x)))) if (!e || o.maxHp > e.maxHp || (o.maxHp === e.maxHp && o.id < e.id)) e = o;
    if (!e) {
      const p = c.cmd.id ? findEnemy(s, c.cmd.id) : null;
      e = p && targetable(s, p) && withinRange(h.x, h.y, p.x, p.y, sk.castRange) ? p : nearestEnemy(s, h.x, h.y, sk.castRange);
    }
    c.e = e;
    return !!e;
  },
  /** The ally to bless: the one asked for, else the one nearest to a fight, else the paladin itself. */
  blessPick: (c) => {
    const { s, h, sk } = c;
    let t = c.cmd.id ? findHero(s, c.cmd.id) : null;
    if (!t || t.downTicks > 0 || !withinRange(h.x, h.y, t.x, t.y, sk.castRange)) {
      t = s.heroes.find((o) => o.id !== h.id && o.downTicks === 0 && withinRange(h.x, h.y, o.x, o.y, sk.castRange) && nearestEnemy(s, o.x, o.y, 3000)) ?? h;
    }
    c.a = t;
    return true;
  },
  /** The wolf runs faster (`n` of its speed; once: a recast only renews it). */
  wolfSpeed: ({ h }, bp) => {
    if (!h.wolf || !h.wolfBase) {
      h.wolfBase = h.stats.speed;
      h.stats.speed = mulBp(h.stats.speed, bp);
    }
    return true;
  },
  /** Reinforce: the buildings around get back a share (`n`) of their life. */
  reinforce: ({ s, h, sk }, bp) => {
    for (const st of s.structures) {
      if (st.destroyed || st.maxHp <= 0) continue;
      const w = hexToWorld(st.q, st.r);
      if (withinRange(h.x, h.y, w.x, w.y, sk.radius)) st.hp = imin(st.maxHp, st.hp + mulBp(st.maxHp, bp));
    }
    return true;
  },
};

